import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal centralizado.
 *
 * Existe ao lado do `Painel` lateral, e a diferença não é estética. O painel
 * serve para EDITAR ao lado da lista — dá para conferir o que já existe
 * enquanto se digita. O modal serve para ESCOLHER: escolher um produto exige a
 * tela inteira e a atenção inteira, e o que está atrás não ajuda em nada.
 *
 * Largura em `max-w` e não fixa: o seletor de produto precisa de espaço para
 * código, descrição, grupo, preço e estoque na mesma linha; um "confirma?"
 * precisa de um terço disso.
 *
 * Fecha no Escape e no clique fora — os dois porque são os dois reflexos, e
 * quem não achar o X vai tentar um deles.
 */
export function Modal({
  aberto,
  titulo,
  descricao,
  tamanho = 'medio',
  aoFechar,
  children,
  rodape,
}: {
  aberto: boolean;
  titulo: string;
  descricao?: string;
  tamanho?: 'pequeno' | 'medio' | 'grande';
  aoFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
}) {
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  const largura = {
    pequeno: 'max-w-md',
    medio: 'max-w-2xl',
    grande: 'max-w-5xl',
  }[tamanho];

  // Portal para o `body`: quem rola agora é o `<main>`, e um overlay
  // renderizado DENTRO dele encadeia a roda do mouse para o fundo — a página
  // andaria atrás do overlay aberto. Fora do scroller não há para onde
  // encadear PORQUE a raiz do Shell é `h-dvh overflow-hidden` e `html, body`
  // têm `overscroll-behavior: none` (estilos.css). O `overscroll-contain`
  // abaixo vale só para o scroller de conteúdo: a cortina não é container de
  // rolagem, e nela a propriedade seria inerte.
  return createPortal(
    <div className="rolagem-fina fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain p-4 sm:p-6">
      {/* Cortina em `--color-palco`: precisa ficar escura nos dois temas, não
          seguir `tinta` (que inverte e viraria um véu claro no escuro). */}
      <div className="fixed inset-0 bg-palco/50" onClick={aoFechar} aria-hidden />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={`bg-superficie border-tinta-100 relative z-10 my-auto flex max-h-[88vh] w-full ${largura} flex-col rounded-xl border shadow-2xl`}
      >
        <header className="border-tinta-100 flex items-start gap-3 border-b px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-tinta-900 text-lg font-semibold">{titulo}</h2>
            {descricao && <p className="text-tinta-500 pt-0.5 text-sm">{descricao}</p>}
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="text-tinta-500 hover:bg-marca-600/10 hover:text-tinta-900 inline-flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        {/* O corpo é quem rola, não a página: o cabeçalho e o rodapé do modal
            ficam parados, e o botão de confirmar não some ao rolar a lista. */}
        <div className="rolagem-fina min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {children}
        </div>

        {rodape && (
          <footer className="border-tinta-100 flex items-center justify-end gap-2 border-t px-5 py-3">
            {rodape}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
